import { Alert, Text, Stack, Group, Badge, Anchor, Box } from "@mantine/core";
import { IconSend } from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useCaseSendOuts } from "../../hooks/useSendOuts";
import { SendOut, SendoutStatus } from "../../services/sendOuts.service";

dayjs.extend(relativeTime);

// ==================== HELPERS ====================

/**
 * Filter send-outs that should block silent duplicate external opinion creation.
 * Excludes: fully received and external stain send-outs.
 * External stain send-outs have stainProviderId set and are shown in PendingStainsPanel instead.
 */
export function filterActiveSendOuts(
  sendOuts: SendOut[] | undefined
): SendOut[] {
  if (!sendOuts) return [];
  return sendOuts.filter(
    (so) => so.status !== SendoutStatus.RECEIVED && !so.stainProviderId
  );
}

function getStatusLabel(status: SendoutStatus): string {
  switch (status) {
    case SendoutStatus.PENDING_APPROVAL:
      return "Pending Approval";
    case SendoutStatus.REJECTED:
      return "Rejected";
    case SendoutStatus.PENDING_SHIPPING:
      return "Pending Shipping";
    case SendoutStatus.SHIPPED:
      return "Shipped";
    case SendoutStatus.RECEIVED_PARTIALLY:
      return "Partially Received";
    case SendoutStatus.RECEIVED:
      return "Received";
    default:
      return status;
  }
}

function getStatusColor(status: SendoutStatus): string {
  switch (status) {
    case SendoutStatus.PENDING_APPROVAL:
      return "orange";
    case SendoutStatus.REJECTED:
      return "red";
    case SendoutStatus.PENDING_SHIPPING:
      return "yellow";
    case SendoutStatus.SHIPPED:
      return "blue";
    case SendoutStatus.RECEIVED_PARTIALLY:
      return "cyan";
    default:
      return "gray";
  }
}

// ==================== HOOK ====================

export function useActiveSendOuts(caseId: string | undefined) {
  const { data: sendOuts, isLoading, error } = useCaseSendOuts(caseId);
  const activeSendOuts = filterActiveSendOuts(sendOuts);

  return {
    activeSendOuts,
    hasActiveSendOuts: activeSendOuts.length > 0,
    isLoading,
    error,
  };
}

// ==================== COMPONENT ====================

interface ActiveSendOutsBannerProps {
  caseId: string | undefined;
  compact?: boolean;
  onViewDetails?: (sendOutId: string) => void;
}

export default function ActiveSendOutsBanner({
  caseId,
  compact = false,
  onViewDetails,
}: ActiveSendOutsBannerProps) {
  const { activeSendOuts, hasActiveSendOuts, isLoading } =
    useActiveSendOuts(caseId);

  if (isLoading || !hasActiveSendOuts) {
    return null;
  }

  if (compact) {
    return (
      <Stack gap={8}>
        {activeSendOuts.map((sendOut) => (
          <Box key={sendOut.id}>
            <Group justify="space-between" wrap="nowrap" gap="xs">
              <Text size="sm" fw={500} lineClamp={1}>
                {sendOut.externalInstitution?.name || "Unknown Institution"}
              </Text>
              <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                {getStatusLabel(sendOut.status)}
              </Text>
            </Group>
            <Text size="xs" c="dimmed">
              Requested {dayjs(sendOut.createdAt).fromNow()}
            </Text>
          </Box>
        ))}
      </Stack>
    );
  }

  const title =
    activeSendOuts.length === 1
      ? "External Opinion Pending"
      : `${activeSendOuts.length} External Opinion Requests Pending`;

  return (
    <Alert
      icon={<IconSend size={20} />}
      color="violet"
      variant="light"
      title={title}
      mb="md"
    >
      <Stack gap="xs">
        {activeSendOuts.map((sendOut) => (
          <Group
            key={sendOut.id}
            justify="space-between"
            wrap="nowrap"
            gap="xs"
          >
            <Text size="sm" lineClamp={1}>
              {sendOut.externalInstitution?.name || "Unknown Institution"}
              <Text span c="dimmed" ml={4}>
                — {dayjs(sendOut.createdAt).fromNow()}
              </Text>
            </Text>
            <Group gap={4} wrap="nowrap">
              <Badge
                size="sm"
                color={getStatusColor(sendOut.status)}
                variant="light"
              >
                {getStatusLabel(sendOut.status)}
              </Badge>
              {onViewDetails && (
                <Anchor
                  component="button"
                  size="sm"
                  onClick={() => onViewDetails(sendOut.id)}
                >
                  View
                </Anchor>
              )}
            </Group>
          </Group>
        ))}
      </Stack>
    </Alert>
  );
}
